import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormDropdown from 'components/KNForm/KNFormDropdown'
import KNFormTags from 'components/KNForm/KNFormTags'
import KNFormText from 'components/KNForm/KNFormText'
import { type Company, CompanyType } from 'context/authentication/Company.types'
import { processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import type { WithId } from 'global/helpers/requests'
import { type ReactElement, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { COMPANIES_QUERY_KEY, createCompany, editCompany } from './CompanyManager.service'
import type { CreateOrEditCompanyFormValues } from './CompanyManager.types'

interface CreateOrEditCompanyDialogPayload {
  readonly company: Company
}

interface CreateOrEditCompanyDialogProps {
  readonly payload?: CreateOrEditCompanyDialogPayload
  readonly open: boolean
  readonly onAction: () => void
  readonly onClose: () => void
}

const CreateOrEditCompanyDialog = ({
  payload,
  open,
  onAction,
  onClose,
}: CreateOrEditCompanyDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, formState, setError } = useForm<CreateOrEditCompanyFormValues>()
  const queryClient = useQueryClient()
  const createCompanyMutation = useMutation({
    mutationFn: createCompany,
    onError: (error) => setError('root', processServerErrorMessages(error)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [COMPANIES_QUERY_KEY] }),
  })
  const updateCompanyMutation = useMutation({
    mutationFn: (data: WithId<CreateOrEditCompanyFormValues>) => editCompany(data.id, data),
    onError: (error) => setError('root', processServerErrorMessages(error)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [COMPANIES_QUERY_KEY] }),
  })

  useEffect(() => {
    if (open) {
      reset(
        processDefaultValues({
          type: payload?.company.type ?? CompanyType.Carrier,
          displayName: payload?.company.displayName,
          customerIdentifiers: payload?.company.customerIdentifiers ?? [],
          properties: {},
        }),
      )
    }
  }, [open])

  const onSubmit: SubmitHandler<CreateOrEditCompanyFormValues> = async (data: CreateOrEditCompanyFormValues) => {
    if (payload) {
      await updateCompanyMutation.mutateAsync({ ...data, id: payload.company.cid })
    } else {
      await createCompanyMutation.mutateAsync(data)
    }
    onAction()
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={
        payload
          ? `${payload.company.displayName} — ${t('screens.shared.company_manager.add_edit_company.edit_details')}`
          : t('screens.shared.company_manager.add_edit_company.new_company')
      }
      actions={
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          loading={formState.isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {payload
            ? t('screens.shared.company_manager.add_edit_company.update')
            : t('screens.shared.company_manager.add_edit_company.create')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Stack spacing={2}>
        <KNFormText
          name="displayName"
          label={t('screens.shared.company_manager.add_edit_company.display_name')}
          control={control}
          rules={{
            required: t('form.validation.required'),
          }}
        />
        <KNFormTags
          name="customerIdentifiers"
          label={t('screens.shared.company_manager.add_edit_company.customer_ids')}
          control={control}
          rules={{
            required: t('form.validation.required'),
          }}
        />
        <KNFormDropdown
          name="type"
          label={t('screens.shared.company_manager.add_edit_company.type')}
          control={control}
          rules={{
            required: t('form.validation.required'),
          }}
          options={Object.values(CompanyType)
            .filter((type) => type !== CompanyType.Operator)
            .map((type) => ({
              value: type,
              label: t(`screens.shared.company_manager.company_types.${type}`),
            }))}
        />
      </Stack>
    </KNDialog>
  )
}

export default CreateOrEditCompanyDialog
